/**
 * Interface for the properties required to calculate the take profit or stop loss percentage.
 */
export interface TakeProfitStopLossPercentageProps {
  targetPrice: number;
  isLong: boolean;
}

/**
 * Calculates the percentage change required to reach the target price from the entry price,
 * adjusted for leverage. This function can be used to determine the take profit or stop loss
 * percentage for both long and short positions.
 *
 * @param {TakeProfitStopLossPercentageProps & { entryPrice: number; leverage: number }} props - The properties required for the calculation.
 * @param {number} props.targetPrice - The target price for the take profit or stop loss.
 * @param {number} props.entryPrice - The entry price of the position.
 * @param {number} props.leverage - The leverage used in the position.
 * @param {boolean} props.isLong - Indicates whether the position is long (true) or short (false).
 * @returns {number} - The percentage change required to reach the target price, adjusted for leverage.
 *
 * @example
 * // Calculate take profit percentage for a long position
 * const percentage = getTakeProfitStopLossPercentage({
 *   targetPrice: 110,
 *   entryPrice: 100,
 *   leverage: 10,
 *   isLong: true,
 * });
 *
 * @example
 * // Calculate stop loss percentage for a short position
 * const percentage = getTakeProfitStopLossPercentage({
 *   targetPrice: 110,
 *   entryPrice: 100,
 *   leverage: 10,
 *   isLong: false,
 * });
 */

export const getTakeProfitStopLossPercentage = ({
  targetPrice,
  entryPrice,
  leverage,
  isLong,
  spreadPercentage,
}: TakeProfitStopLossPercentageProps & {
  entryPrice: number;
  leverage: number;
  spreadPercentage: number;
}) => {
  // Just check isLong is enough, the minus result is negative or not will reflect take profit / stop loss feature
  const deltaPrice = isLong
    ? targetPrice * (1 - spreadPercentage) - entryPrice
    : entryPrice - targetPrice * (1 + spreadPercentage);

  return (deltaPrice / entryPrice) * leverage;
};
